import { FC, useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { ObraActions } from 'Store/Obra/Obra.actions';
import { TenantActions } from 'Store/Tenant/Tenant.actions';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import { useQueryObra } from 'Hooks/obras/useQueryObra';
import { useQueryUniqueObra } from 'Hooks/obras/useQueryUniqueObra';
import { useQueryCreateObra } from 'Hooks/obras/useQueryCreateObra';
import { useQueryUpdateObra } from 'Hooks/obras/useQueryUpdateObra';
import { useQueryUpdateArchivedObra } from 'Hooks/obras/useQueryUpdateArchivedObra';
import { useQueryUpdateNomenclatureObra } from 'Hooks/obras/useQueryUpdateNomenclatureObra';
import { IInvitedUser } from 'Data/interfaces/User/IInvitedUser';
import { ISharedUser } from 'Data/interfaces/User/ISharedUser';
import { IUserData } from 'Data/interfaces/User/IUserData';
import { IArchivedStatusRequest } from 'Data/interfaces/Obra/IArchivedStatusRequest';
import { ICreateObra, INomenclatureObraData, IUpdateObra } from 'Data/interfaces/Obra/ICreateOrUpdate';
import { IObraNomenclaturesRequest } from 'Data/interfaces/Obra/IObraNomenclaturas';
import CadastroTab from 'Pages/Obras/components/Tabs/CadastroTab';
import DisciplinasTab from 'Pages/Obras/components/Tabs/DisciplinasTab';
import UsuariosConvidadosTab from 'Pages/Obras/components/Tabs/UsuariosConvidadosTab';
import PermissoesUsuariosTab from 'Pages/Obras/components/Tabs/PermissoesUsuariosTab';
import NomenclaturaTab from 'Pages/Obras/components/Tabs/NomenclaturaTab';
import ArmazenamentoTab from 'Pages/Obras/components/Tabs/ArmazenamentoTab';
import Modal from 'Components/UI/Modal';
import Tabs from 'Components/UI/Tabs/Tabs';
import Button from 'Components/UI/Button';
import AvisoArquivarObra from '../AvisoArquivarObra';
import toastHandler from 'Utils/toastHandler';
import { Mixpanel } from 'Utils/MixPanel';
import { ArchiveConstructionButton, HintNomenclature } from './styles';

interface ICadastroObra {
  isVisible?: boolean;
  isEdit: boolean;
  firstInvitedUsers?: IInvitedUser[];
  isNomenclatureEdit?: boolean;
  constructionSiteId?: number;
  onCancel?: () => void;
}

export interface IUpdatedUsers {
  InvitedUsers: IInvitedUser[];
  SharedUsers: ISharedUser[];
}

const CadastroObra: FC<Props> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);
  const { data: obrasList, isLoading: isLoadingObrasList } = useQueryObra();
  const { data: obra, isLoading: isLoadingObra } = useQueryUniqueObra({
    csId: props?.constructionSiteId ? Number(props.constructionSiteId) : undefined,
    ignoreCache: true,
    navigate,
  });
  const { createObra, isLoading: isLoadingCreateObra } = useQueryCreateObra({ navigate, dispatch });
  const { updateObra, isLoading: isLoadingUpdateObra } = useQueryUpdateObra({ dispatch });
  const { updateArchivedObra, isLoading: isLoadingUpdateArchivedObra } = useQueryUpdateArchivedObra({
    navigate,
    dispatch,
  });
  const { updateNomenclatureObra } = useQueryUpdateNomenclatureObra();

  const [obraData, setObraData] = useState<any>({});
  const [tabSelected, setTabSelected] = useState(0);
  const [tabEditSubmit, setTabEditSubmit] = useState(0);
  const [isSubmited, setSubmit] = useState(false);
  const [updatedUsers, setUpdatedUsers] = useState<IUpdatedUsers>({
    InvitedUsers: obra?.InvitedUsers || [],
    SharedUsers: obra?.SharedUsers || [],
  });
  const [closeModalAfterSubmit, setCloseModalAfterSubmit] = useState(false);
  const [arquivarObraVisible, setArquivarObraVisible] = useState(false);
  const [TenantActiveSharedUsers, setTenantActiveSharedUsers] = useState<IUserData[]>([]);

  const isLoading = useMemo(() => {
    return props.isLoading ||
      props.tenantIsLoading ||
      isLoadingObrasList ||
      isLoadingObra ||
      isLoadingCreateObra ||
      isLoadingUpdateObra ||
      isLoadingUpdateArchivedObra;
  }, [
    props.isLoading,
    props.tenantIsLoading,
    isLoadingObrasList,
    isLoadingObra,
    isLoadingCreateObra,
    isLoadingUpdateObra,
    isLoadingUpdateArchivedObra,
  ]);

  if (closeModalAfterSubmit && !isLoading) {
    setCloseModalAfterSubmit(false);
    if (props.onCancel) {
      props.onCancel();
    }
  }

  const submitEdit = (values?: object, errors?: string[]) => {
    if (submit(values, errors)) {
      submitNextTab();
    } else {
      setTabSelected(tabEditSubmit);
      resetSubmitEdit();
    }
  };

  const submitNextTab = () => {
    if (tabEditSubmit < getTabs().length - 1) {
      setTabEditSubmit(tabEditSubmit + 1);
      setSubmit(true);
    } else {
      setCloseModalAfterSubmit(true);
    }
  };

  const resetSubmitEdit = () => {
    setSubmit(false);
    setTabEditSubmit(0);
  };

  const submitConfirm = (values?: object, errors?: string[]) => {
    if (submit(values, errors)) {
      if (tabSelected < getTabs().length - 1) {
        setTabSelected(tabSelected + 1);
      } else {
        setCloseModalAfterSubmit(true);
      }
    }
  };

  const handleNomenclatureSubmit = (values?: INomenclatureObraData) => {
    if (!values || !props?.constructionSiteId) return;

    const request: IObraNomenclaturesRequest = {
      constructionStieId: Number(props.constructionSiteId),
      nomenclatures: {
        isVersionControlDefault: values.isVersionControlDefault,
        versionConfig: {
          hasRevisionControl: values.VersionConfig.HasRevisionControl,
          versionFields: values.VersionConfig.VersionFields.map((field: any) => ({
            ordem: field.Ordem,
            campoPersonalizado: field.CampoPersonalizado,
            separador: field.Separador,
            campoVersaoId: field.CampoVersaoId,
          })),
        },
      },
    };
    updateNomenclatureObra(request);
  }

  const submit = (values?: any, errors?: string[]) => {
    setSubmit(false);
    if (errors) {
      errors.forEach((error) => {
        toastHandler.handler({ description: error, icon: 'error', title: '' });
      });
      return false;
    } else {
      const tabIndex = obra?.ConstructionSiteId ? tabEditSubmit : tabSelected;
      const shouldData = tabIndex === getTabs().length - 1;
      if (values) {
        if (!shouldData) {
          setObraData((prev: any) => ({ ...prev, ...values }));
        }
        if (shouldData) {
          if (props.isNomenclatureEdit) {
            handleNomenclatureSubmit(values);
          } else {
            const isFirstObra = obrasList.length <= 0;
            if (obra?.ConstructionSiteId) {
              const updateObraData: IUpdateObra = {
                values: {
                  ...obraData,
                  ...values,
                  ConstructionSiteId: obra.ConstructionSiteId,
                },
                firstObraTracking: isFirstObra,
                userInfoTracking: props.userInfo,
                currentTenantTracking: currentTenant,
              };
              updateObra(updateObraData);
            } else {
              const createObraData: ICreateObra = {
                values: {
                  ...obraData,
                  ...values,
                },
                firstObraTracking: isFirstObra,
                userInfoTracking: props.userInfo,
                currentTenantTracking: currentTenant,
              };
              createObra(createObraData);
            }
          }
        }
      }
    }
    return true;
  };

  const showTabUserPermissions = useCallback(() => {
    return (updatedUsers?.InvitedUsers && updatedUsers?.InvitedUsers.length > 0) ||
      (updatedUsers?.SharedUsers && updatedUsers?.SharedUsers.length > 0);
  }, [updatedUsers]);

  const openUpgradePremium = () => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'convidar-usuarios-modal',
        originPath: window.location.pathname,
      },
      userInfo: props.userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });

    setCloseModalAfterSubmit(true);
    if (props.currentRole === 1) {
      navigate('/faturamento/planos');
    } else {
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  };

  const getTabs = useCallback(() => {
    if (props.isEdit) {
      return [{ title: 'Nomenclatura' }];
    }

    const tabs = [
      { title: 'Cadastro' },
      { title: 'Disciplinas' },
      { title: 'Usuários' },
    ];

    if (showTabUserPermissions()) {
      tabs.push({ title: 'Permissões' });
    }

    tabs.push({ title: 'Nomenclatura' });
    tabs.push({ title: 'Armazenamento' });

    return tabs;
  }, [showTabUserPermissions, props.isEdit]);

  const getTab = (title: string, index: any) => {
    const tabs = new Map();
    const onSubmitTab = obra?.ConstructionSiteId
      ? submitEdit
      : submitConfirm;
    const isSubmitedTab = isSubmited && (obra?.ConstructionSiteId
      ? tabEditSubmit === index
      : tabSelected === index
    );

    tabs.set(
      'Cadastro',
      <CadastroTab
        isSubmited={isSubmitedTab}
        onSubmit={onSubmitTab}
        name={obra?.Name}
        type={obra?.Type}
        address={obra?.Address}
        avatarFileName={obra?.AvatarFileName}
        listObras={obrasList}
        constructionSiteId={obra?.ConstructionSiteId}
      />,
    );
    tabs.set(
      'Disciplinas',
      <DisciplinasTab
        isSubmited={isSubmitedTab}
        onSubmit={onSubmitTab}
      />,
    );
    tabs.set(
      'Usuários',
      <UsuariosConvidadosTab
        isSubmited={isSubmitedTab}
        onSubmit={onSubmitTab}
        tenantRoles={props.tenantRoles}
        onConfirm={openUpgradePremium}
        registerCompleted={props.registerCompleted}
        TenantSharedUsers={TenantActiveSharedUsers}
        sharedUsers={updatedUsers.SharedUsers}
        invitedUsers={updatedUsers.InvitedUsers}
        changeUsers={setUpdatedUsers}
        currentRole={props.currentRole}
        numberInvitedAllUsers={props.userLimitsActive?.QtyActiveUsers}
        maxUsers={props.userInfo?.UserStoreItem.MaxQtyUsers ||
          props.userInfo?.UserStoreItem.StorePlan?.MaxQtyUsers
        }
        csId={obra?.ConstructionSiteId}
        firstInvitedUsers={props.firstInvitedUsers}
      />,
    );
    tabs.set(
      'Permissões',
      <PermissoesUsuariosTab
        isSubmited={isSubmitedTab}
        onSubmit={onSubmitTab}
        invitedUsers={updatedUsers.InvitedUsers}
        sharedUsers={updatedUsers.SharedUsers}
        changeUsers={setUpdatedUsers}
      />,
    );
    tabs.set(
      'Nomenclatura',
      <NomenclaturaTab
        isSubmited={isSubmitedTab}
        isFirstObra={obrasList.length <= 0}
        obraVersionData={obra?.VersionConfig}
        versionConfigFk={obra?.VersionConfigFk}
        onSubmit={onSubmitTab}
      />,
    );
    tabs.set(
      'Armazenamento',
      <ArmazenamentoTab
        isSubmited={isSubmitedTab}
        onSubmit={onSubmitTab}
        accountsInfo={props.AccountsInfo}
      />,
    );

    return (
      <span>
        <div style={{ padding: 20 }} hidden={!isLoading}>
          <Skeleton loading={true} paragraph={{ rows: 4 }} />
        </div>
        <div hidden={isLoading}>
          {tabs.get(title)}
        </div>
      </span>
    );
  };

  const getTitleModal = () => {
    return props.isEdit ? 'Edição de obra' : 'Cadastrar Obras';
  };

  const isTabDisabled = (indexTab: number) => {
    return indexTab >= tabSelected && !props.isEdit;
  };

  const showButtonArchiveConstruction = () => {
    return props.isEdit &&
      !isLoading &&
      getTabs()[tabSelected].title === 'Cadastro' && (
        <ArchiveConstructionButton onClick={() => setArquivarObraVisible(true)}>
          Arquivar obra
        </ArchiveConstructionButton>
      );
  };

  const showHintNomenclature = () => {
    return getTabs()[tabSelected].title === 'Nomenclatura' && (
      <HintNomenclature>
        Para configurações avançadas acesse Configurar serviço &gt; Nomeclatura
      </HintNomenclature>
    );
  };

  const arquivarObra = () => {
    const status: IArchivedStatusRequest = {
      ConstructionSiteId: obra?.ConstructionSiteId || 0,
      isArchived: true,
    };
    updateArchivedObra(status);

    const obras = obrasList?.filter((obraList) => !obraList.isArchived);
    if (obras && obras.length === 1) {
      dispatch(ObraActions.clearConstructionSiteSearch(true));
    }

    setArquivarObraVisible(false);
    if (props.onCancel) {
      props.onCancel();
    }
  };

  useEffect(() => {
    setTabSelected(0);
    setTabEditSubmit(0);
    setSubmit(false);
  }, [props.isVisible]);

  useEffect(() => {
    dispatch(TenantActions.getUsers());
    dispatch(TenantActions.getTenantListUsersConstruction());
  }, []);

  useEffect(() => {
    if (obra?.SharedUsers) {
      obra.SharedUsers.forEach((sharedUser: ISharedUser) => {
        return sharedUser.UserId = sharedUser.UserFk;
      });
    }
    setUpdatedUsers({
      InvitedUsers: [...obra?.InvitedUsers || []],
      SharedUsers: [...obra?.SharedUsers || []],
    });
  }, [obra?.SharedUsers, obra?.InvitedUsers]);

  useEffect(() => {
    if (props.TenantUsers?.tenantUsers) {
      setTenantActiveSharedUsers([
        ...props.TenantUsers.tenantUsers.filter((tenant) => tenant.isActive === true).map((tenant) => {
          return { ...tenant.User, CurrentRoleFk: tenant.Role.TenantRoleId };
        }),
      ]);
    }
  }, [props.TenantUsers]);

  useEffect(() => {
    if (getTabs()[tabSelected].title === 'Armazenamento' && !props.AccountsInfo) {
      dispatch(ObraActions.listConstructionSiteAccounts());
    }
  }, [tabSelected, props.AccountsInfo, dispatch, getTabs]);

  return (
    <>
      <Modal
        width={800}
        title={getTitleModal()}
        visible={props.isVisible}
        onCancel={props.onCancel}
        maskClosable={false}
        footerButtons={(
          <>
            {showButtonArchiveConstruction()}
            {showHintNomenclature()}
            <Button onClick={props.onCancel}>
              <span style={{ padding: '0 35px' }}>Cancelar</span>
            </Button>
            <Button
              type='primary'
              loading={isLoading}
              onClick={() => setSubmit(true)}
            >
              <span style={{ padding: '0 35px' }}>
                {tabSelected === getTabs().length - 1 || props.isEdit ? 'Concluir' : 'Continuar'}
              </span>
            </Button>
          </>
        )}
      >
        {isLoadingObra ? (
          <div style={{ padding: 20 }}>
            <Skeleton loading={true} paragraph={{ rows: 4 }} />
          </div>
        ) : (
          <Tabs
            tabs={getTabs().map((tab, index) => ({
              ...tab,
              key: index.toString(),
              disabled: isTabDisabled(index),
              children: <span>{getTab(tab.title, index)}</span>,
              forceRender: props.isEdit,
            }))}
            activeKey={tabSelected.toString()}
            onTabClick={(key: string) => { setTabSelected(+key); }}
          />
        )}
      </Modal>

      {props.isEdit && (
        <AvisoArquivarObra
          nomeObra={obra?.Name}
          visible={arquivarObraVisible}
          onCancel={() => setArquivarObraVisible(false)}
          onConfirm={arquivarObra}
        />
      )}
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.obra,
  userInfo: state.auth.userInfo,
  userLimitsActive: state.auth.userLimitsActive,
  tenantAllUsers: state.tenant.UsersWithConstruction,
  currentRole: state.auth.userInfo?.CurrentRoleFk,
  tenantIsLoading: state.tenant.isLoading,
  tenantRoles: [...(state.tenant.TenantRoles || [])],
  TenantSharedUsers: [...(state.tenant.Users || [])],
  registerCompleted: state.minhaConta.userInfo?.isRegisterComplete,
  TenantUsers: state.tenant.UsersWithConstruction,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & ICadastroObra;

export default connector(CadastroObra);
